import { sha256 } from 'js-sha256';
import type React from 'react';

import { type FlashMessageProps } from '@/atoms/flashMessages/flashMessagesState';
import { type Brand } from '@/consts/brands';
import {
  TreatmentCategoryIds,
  type TreatmentCategory,
} from '@/consts/treatmentCategories';
import { centsToDollarsAndCents as centsToDollarsAndCentsHelper } from '@/helpers/currencyUtils/helper';
import {
  type Card,
  type Flags,
  type ItemPrice,
  type ItemPrices,
  type Patient,
  type PromotionClaim,
} from '@/types/productSelectionAndCheckout/types';
import { type PurchaseContext } from '@/types/purchaseContextToggle/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';
import { canonicalPath } from '@/utils/metrics/pages';

export const centsToDollarsAndCents = centsToDollarsAndCentsHelper;

export const extractPatientData = (patient: Patient) => {
  return {
    address1: patient.address1,
    address2: patient.address2,
    brand: patient.brand,
    city: patient.city,
    email: patient.email,
    firstname: patient.firstname,
    hashedEmail: sha256(patient.email),
    id: patient.id,
    lastname: patient.lastname,
    phone: patient.phone,
    sessionId: patient.sessionId,
    zipcode: patient.zipcode,
  };
};

export const getCardFullSizeItemPrices = (
  card: Card,
  fullSizeItemPrices: ItemPrices,
) => {
  if (card.fullSizeItemId) {
    return fullSizeItemPrices[card.fullSizeItemId];
  }

  if (card.fullSizeItemIds) {
    const cardFullSizePrices = {
      adjustmentAmount: 0,
      finalAmount: 0,
      originalAmount: 0,
    };

    for (let index = 0; index < card.fullSizeItemIds.length; index += 1) {
      const fullSizeItemId = card.fullSizeItemIds[index];
      const fullSizeItemPrice = fullSizeItemPrices[fullSizeItemId];

      cardFullSizePrices.adjustmentAmount +=
        fullSizeItemPrice?.adjustmentAmount || 0;

      cardFullSizePrices.finalAmount += fullSizeItemPrice?.finalAmount || 0;

      cardFullSizePrices.originalAmount +=
        fullSizeItemPrice?.originalAmount || 0;
    }

    return cardFullSizePrices;
  }
};

export const getCardTrialSizeItemPrices = (
  card: Card,
  isTwoMonthFirstShipment?: boolean,
  trialSizeItemPrices?: ItemPrices,
) => {
  const size = isTwoMonthFirstShipment ? 'fullSizeItemId' : 'trialSizeItemId';
  const sizes = isTwoMonthFirstShipment
    ? 'fullSizeItemIds'
    : 'trialSizeItemIds';

  if (card[size]) {
    // @ts-expect-error - TODO: broken by strictNullChecks
    return trialSizeItemPrices?.[card[size]];
  }

  if (card[sizes]) {
    const cardTrialSizePrices = {
      adjustmentAmount: 0,
      finalAmount: 0,
      originalAmount: 0,
    };

    // @ts-expect-error - TODO: broken by strictNullChecks
    for (let index = 0; index < card[sizes].length; index += 1) {
      // @ts-expect-error - TODO: broken by strictNullChecks
      const trialSizeItemId = card[sizes][index];
      const trialSizeItemPrice = trialSizeItemPrices?.[trialSizeItemId];

      cardTrialSizePrices.adjustmentAmount +=
        trialSizeItemPrice?.adjustmentAmount || 0;

      cardTrialSizePrices.finalAmount += trialSizeItemPrice?.finalAmount || 0;

      cardTrialSizePrices.originalAmount +=
        trialSizeItemPrice?.originalAmount || 0;
    }

    return cardTrialSizePrices;
  }
};

export const getLineItemCards = (
  card: Card,
  itemPrices: ItemPrices,
  subscriptionItemPrices: ItemPrices,
  useFullSizeItemIds?: boolean,
  purchaseContext?: PurchaseContext,
  onetimeItemPrices?: ItemPrices,
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const isOnetime = purchaseContext === 'onetime';
  const effectiveItemPrices =
    isOnetime && onetimeItemPrices ? onetimeItemPrices : itemPrices;
  const effectiveSubscriptionPrices = isOnetime
    ? undefined
    : subscriptionItemPrices;

  console.log(isOnetime);
  const size = useFullSizeItemIds ? 'fullSizeItemId' : 'trialSizeItemId';
  const subscriptionSize = 'fullSizeItemId';
  const sizes = useFullSizeItemIds ? 'fullSizeItemIds' : 'trialSizeItemIds';
  const subscriptionSizes = 'fullSizeItemIds';
  const lineItemCards = [];

  if (card[size]) {
    lineItemCards.push({
      id: card.id,
      isFullSize: !!useFullSizeItemIds,
      isRx: card.isRx,
      // @ts-expect-error - TODO: broken by strictNullChecks
      itemPrices: effectiveItemPrices[card[size]],
      subscriptionItemPrices:
        card[subscriptionSize] && effectiveSubscriptionPrices
          ? effectiveSubscriptionPrices[card[subscriptionSize]]
          : undefined,
      name: card.lineItemName || card.name,
      notMonthly: card.notMonthly,
      thumbnailImage: card.thumbnailImage,
      volume: card.volume,
    });
  } else if (card[sizes]) {
    // @ts-expect-error - TODO: broken by strictNullChecks
    for (let index = 0; index < card[sizes].length; index += 1) {
      const lineItemName = card.lineItemNames?.[index];
      const thumbnailImage = card.thumbnailImages?.[index];
      // @ts-expect-error - TODO: broken by strictNullChecks
      const itemId = card[sizes][index];
      const subscriptionItemId = card[subscriptionSizes]?.[index];
      const volume = card.volumes?.[index];

      lineItemCards.push({
        id: itemId.toString(),
        isFullSize: !!useFullSizeItemIds,
        isRx: card.isRx,
        itemPrices: effectiveItemPrices[itemId],
        subscriptionItemPrices:
          subscriptionItemId && effectiveSubscriptionPrices
            ? effectiveSubscriptionPrices[itemId]
            : undefined,
        name: lineItemName,
        notMonthly: card.notMonthly,
        thumbnailImage: thumbnailImage,
        volume: volume,
        modal: card.modal,
      });
    }
  } else if (card.fullSizeItemId) {
    // catch for full sized items in 1 month trial (e.g. lipbalm)
    lineItemCards.push({
      id: card.id,
      isFullSize: true,
      isRx: card.isRx,
      itemPrices: {
        finalAmount:
          isOnetime && onetimeItemPrices
            ? onetimeItemPrices[card.fullSizeItemId]?.finalAmount
            : effectiveItemPrices[card.fullSizeItemId]?.finalAmount / 2,
      },
      subscriptionItemPrices: undefined,
      name: card.lineItemName || card.name,
      notMonthly: card.notMonthly,
      thumbnailImage: card.thumbnailImage,
      volume: card.volume,
      modal: card.modal,
    });
  }

  return lineItemCards;
};

export const getSalePrice = (cardTrialSizeItemPrices: ItemPrice) => {
  if (cardTrialSizeItemPrices.finalAmount === 0) {
    return 'Free';
  }

  return centsToDollarsAndCents(cardTrialSizeItemPrices.finalAmount);
};

export const getSalePriceSubscription = (card: Card, flags: Flags) => {
  if (!card.notMonthly && !flags.isTwoMonthFirstShipment) {
    return '';
  }

  return undefined;
};

export const getDisplayPrices = (
  trialPrice?: ItemPrice,
  fullSizePrice?: ItemPrice,
  promotionClaim?: PromotionClaim,
  purchaseContext?: PurchaseContext,
) => {
  const hasAdjustment = (trialPrice?.adjustmentAmount || 0) > 0;
  console.log(trialPrice);
  console.log(fullSizePrice);
  const firstMonthPrice =
    (hasAdjustment ? trialPrice?.originalAmount : trialPrice?.finalAmount) || 0;
  let price;
  if (purchaseContext && purchaseContext === 'onetime') {
    price = centsToDollarsAndCents(firstMonthPrice);
  } else {
    price = centsToDollarsAndCents(
      fullSizePrice ? fullSizePrice.originalAmount / 2 : firstMonthPrice,
    );
  }
  const salePrice =
    trialPrice && hasAdjustment ? getSalePrice(trialPrice) : undefined;
  const promotionSalePrice =
    trialPrice && promotionClaim
      ? centsToDollarsAndCents(trialPrice.finalAmount - promotionClaim.amount)
      : undefined;

  return { price, salePrice: salePrice || promotionSalePrice };
};

export const getSwipeDirection = (
  currentIndex: number,
  nextIndex: number,
  lastIndex: number,
) => {
  if (currentIndex === 0 && nextIndex === lastIndex) {
    return 'backward';
  }

  if (currentIndex === lastIndex && nextIndex === 0) {
    return 'forward';
  }

  if (currentIndex > nextIndex) {
    return 'backward';
  }

  return 'forward';
};

export const renderFlashMessageOnCheckoutSuccess = (
  flashMessages: FlashMessageProps[],
  setFlashMessages: React.Dispatch<React.SetStateAction<FlashMessageProps[]>>,
) => {
  setFlashMessages([
    ...flashMessages,
    {
      children:
        'Success! You’re one step closer to your personalized Treatment Plan.',
      state: 'success',
    },
  ]);
};

export const isCardSame = (cardA: Card, cardB: Card) => {
  if (cardA.fullSizeItemId && cardB.fullSizeItemId) {
    return cardA.fullSizeItemId === cardB.fullSizeItemId;
  }

  if (cardA.fullSizeItemIds && cardB.fullSizeItemIds) {
    return (
      cardA.fullSizeItemIds.length === cardB.fullSizeItemIds.length &&
      cardA.fullSizeItemIds.every(
        (value, index) => value === cardB.fullSizeItemIds?.[index],
      )
    );
  }

  return false;
};

export const trackProductSelectionPageLoadEvents = (
  brand: Brand,
  treatmentCategory: TreatmentCategory,
) => {
  trackEvent(AnalyticsEvent.RECOMMENDATION_LOADING_STARTED);

  trackEvent(AnalyticsEvent.RECOMMENDATION_DETAILS_VIEWED, {
    path: canonicalPath(),
    brand,
    treatmentCategories: treatmentCategory,
    treatmentCategoryIds: TreatmentCategoryIds[treatmentCategory],
  });
};
